<template>
  <v-card elevation="3" class="mb-3 pb-3 my-5">
    <v-alert class="mx-3" type="warning" variant="outlined" density="compact">
      {{ t("components.siteSettings.manualLimits.adyenWarning") }}
    </v-alert>
    <template #title>
      <div class="d-flex align-center justify-space-between">
        <div>
          <span class="font-weight-black text-capitalize">{{ title }}</span>
        </div>
        <v-btn-nd v-if="mode === Mode.VIEW" variant="text" @click="mode = Mode.EDIT">
          <v-icon>mdi-pencil</v-icon>
        </v-btn-nd>
        <v-btn-nd v-else variant="text" @click="mode = Mode.VIEW">
          <v-icon>mdi-close-circle-outline</v-icon>
        </v-btn-nd>
      </div>
    </template>
    <v-card-text v-if="mode === Mode.EDIT">
      <v-switch
        v-model="limitForm.enabled"
        color="green"
        base-color="red"
        :label="
          limitForm?.enabled
            ? t('components.siteSettings.manualLimits.on')
            : t('components.siteSettings.manualLimits.off')
        "
        :value="true"
        hide-details
      />
      <v-text-field
        v-if="limitForm?.enabled"
        v-model="limitForm.limit"
        type="number"
        :error-messages="
          (v$.limit.$silentErrors.map((e: ErrorObject) => e.$message) as string[]) || []
        "
        :label="t('components.siteSettings.manualLimits.limitLabel')"
        clearable
      />
      <v-text-field
        v-if="limitForm?.enabled && limitKey === 'moto'"
        v-model="limitForm.dailyLimit"
        type="number"
        :error-messages="
          (v$.limit.$silentErrors.map((e: ErrorObject) => e.$message) as string[]) || []
        "
        :label="t('components.siteSettings.manualLimits.dailyLimitLabel')"
        clearable
      />
    </v-card-text>
    <v-card-text v-else>
      <v-switch
        disabled
        :model-value="limit?.enabled"
        :label="getDisplayLabel()"
        :value="true"
        hide-details
      />
    </v-card-text>
    <v-card-actions class="justify-space-between">
      <v-btn :loading="loading" :disabled="!isEditing || v$.$invalid" @click="save">
        {{ t("form.save") }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script setup lang="ts">
import { SiteInternalSettingsMoto } from "@tableyeti/merchant-service";
import useVuelidate, { ErrorObject } from "@vuelidate/core";
import { numeric, requiredIf, minValue } from "@vuelidate/validators";
import countryToCurrency from "country-to-currency";
import { isEqual } from "lodash-es";
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { displayAmount } from "@/utils/format";

const { t } = useI18n();

type SiteManualLimit = SiteInternalSettingsMoto;

enum Mode {
  VIEW = "view",
  EDIT = "edit",
}

const { limit, country, limitKey } = defineProps<{
  title: string;
  limitKey: "moto" | "unreferencedRefunds";
  limit?: SiteManualLimit;
  loading?: boolean;
  country: string;
}>();

const emit = defineEmits<{ updateInternalSettings: [string, SiteManualLimit] }>();
const mode = ref<Mode>(Mode.VIEW);
const limitForm = ref<SiteManualLimit>(Object.assign({}, limit));

const v$ = useVuelidate<SiteManualLimit>(
  {
    limit: {
      $autoDirty: true,
      numeric: numeric,
      requiredIf: requiredIf(() => limitForm.value.enabled),
      minValue: minValue(0),
    },
    ...(limitKey === "moto"
      ? {
          dailyLimit: {
            $autoDirty: true,
            numeric: numeric,
            requiredIf: requiredIf(() => limitForm.value.enabled),
            minValue: minValue(0),
          },
        }
      : {}),
  },
  limitForm,
);

const getDisplayLabel = () => {
  if (!limit || !limit?.enabled) {
    return t("components.siteSettings.manualLimits.off");
  }
  let amount = "";
  let dailyLimit = "";
  if (limit?.limit !== undefined) {
    amount = displayAmount({
      value: limit.limit,
      currency: countryToCurrency[country as keyof typeof countryToCurrency],
    });
  }

  if (limitKey === "moto" && limit?.dailyLimit !== undefined) {
    dailyLimit = displayAmount({
      value: limit.dailyLimit,
      currency: countryToCurrency[country as keyof typeof countryToCurrency],
    });
  }

  if (dailyLimit) {
    return t("components.siteSettings.manualLimits.onWithLimitAndDailyLimit", {
      amount,
      dailyLimit,
    });
  }

  return t("components.siteSettings.manualLimits.onWithLimit", { amount });
};

const save = async () => {
  if (!(await v$.value.$validate())) {
    return;
  }

  const payload: SiteManualLimit = {
    enabled: limitForm.value.enabled,
  };

  if (limitForm.value.enabled) {
    payload.limit = parseInt(limitForm.value.limit?.toString() ?? "0");

    if (limitKey === "moto" && limitForm.value.dailyLimit !== undefined) {
      payload.dailyLimit = parseInt(limitForm.value.dailyLimit?.toString() ?? "0");
    }
  }

  emit("updateInternalSettings", limitKey, payload);
  mode.value = Mode.VIEW;
};

const isEditing = computed(() => !isEqual(limit, limitForm.value));
</script>
